import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface BusinessHour {
  day: string;
  hours: string;
  open: string;
  close: string;
}

export interface DoctorScheduleDay {
  day: string;
  open: string;
  close: string;
}

export interface DoctorEntry {
  id: string;
  name: string;
  schedule: DoctorScheduleDay[];
}

export interface PharmacySettings {
  id: number;
  pharmacy_name: string;
  tagline: string;
  address_line1: string;
  address_line2: string;
  city: string;
  province: string;
  postal_code: string;
  country: string;
  phone: string;
  phone_raw: string;
  fax: string;
  email: string;
  google_maps_embed_url: string;
  latitude: string;
  longitude: string;
  business_hours: BusinessHour[];
  doctor_schedule: DoctorEntry[];
}

interface PharmacyContextValue {
  settings: PharmacySettings | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  updateSettings: (data: Partial<PharmacySettings>) => Promise<{ success: boolean; error?: string }>;
  getFullAddress: () => string;
  getTodayHours: () => { day: string; hours: string } | null;
}

const defaultSettings: PharmacySettings = {
  id: 1,
  pharmacy_name: 'Thornhill Drug-Mart',
  tagline: 'Addiction Treatment & Pharmacy',
  address_line1: '8108 Yonge St Unit 3-4',
  address_line2: '',
  city: 'Thornhill',
  province: 'ON',
  postal_code: 'L4J 1W4',
  country: 'Canada',
  phone: '(905) 709-9985',
  phone_raw: '9057099985',
  fax: '(905) 709-9986',
  email: 'info@thornhilltreatment.com',
  google_maps_embed_url: '',
  latitude: '43.8184',
  longitude: '-79.4278',
  business_hours: [
    { day: 'Monday', hours: '8:00 AM – 12:00 PM', open: '08:00', close: '12:00' },
    { day: 'Tuesday', hours: '8:00 AM – 8:00 PM', open: '08:00', close: '20:00' },
    { day: 'Wednesday', hours: '8:00 AM – 8:00 PM', open: '08:00', close: '20:00' },
    { day: 'Thursday', hours: '8:00 AM – 8:00 PM', open: '08:00', close: '20:00' },
    { day: 'Friday', hours: '8:00 AM – 2:00 PM', open: '08:00', close: '14:00' },
    { day: 'Saturday', hours: '9:00 AM – 6:00 PM', open: '09:00', close: '18:00' },
    { day: 'Sunday', hours: '10:00 AM – 5:00 PM', open: '10:00', close: '17:00' },
  ],
  doctor_schedule: [
    {
      id: 'doc-1',
      name: 'Dr. Martis',
      schedule: [
        { day: 'Monday', open: '08:00', close: '12:00' },
        { day: 'Wednesday', open: '09:00', close: '15:00' },
        { day: 'Thursday', open: '08:00', close: '16:00' },
        { day: 'Saturday', open: '09:00', close: '12:00' },
      ],
    },
    {
      id: 'doc-2',
      name: 'Dr. Taghaddos',
      schedule: [
        { day: 'Tuesday', open: '08:00', close: '16:00' },
        { day: 'Wednesday', open: '08:00', close: '17:00' },
        { day: 'Friday', open: '08:00', close: '14:00' },
        { day: 'Saturday', open: '09:00', close: '12:00' },
      ],
    },
    {
      id: 'doc-3',
      name: 'Dr. Lao',
      schedule: [
        { day: 'Tuesday', open: '08:00', close: '16:00' },
        { day: 'Wednesday', open: '08:00', close: '17:00' },
        { day: 'Thursday', open: '08:00', close: '16:00' },
      ],
    },
    {
      id: 'doc-4',
      name: 'Dr. Salama',
      schedule: [
        { day: 'Wednesday', open: '08:00', close: '17:00' },
      ],
    },
  ],
};

const PharmacyContext = createContext<PharmacyContextValue>({
  settings: defaultSettings,
  loading: false,
  error: null,
  refresh: () => {},
  updateSettings: async () => ({ success: false }),
  getFullAddress: () => '',
  getTodayHours: () => null,
});

export function PharmacyProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<PharmacySettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('pharmacy_settings')
        .select('*')
        .single();

      if (fetchError) throw fetchError;
      if (data) {
        let migratedDoctorSchedule = data.doctor_schedule;
        if (Array.isArray(migratedDoctorSchedule) && migratedDoctorSchedule.length > 0) {
          const first = migratedDoctorSchedule[0];
          if (typeof first.doctors === 'string' && !Array.isArray((first as any).schedule)) {
            migratedDoctorSchedule = defaultSettings.doctor_schedule;
          }
        }
        if (!Array.isArray(migratedDoctorSchedule) || migratedDoctorSchedule.length === 0) {
          migratedDoctorSchedule = defaultSettings.doctor_schedule;
        }

        setSettings({
          ...data,
          business_hours: data.business_hours || defaultSettings.business_hours,
          doctor_schedule: migratedDoctorSchedule,
        });
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch pharmacy settings:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = async (data: Partial<PharmacySettings>): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error: updateError } = await supabase
        .from('pharmacy_settings')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', settings.id);

      if (updateError) throw updateError;

      setSettings((prev) => ({ ...prev, ...data }));
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const getFullAddress = useCallback(() => {
    const parts = [settings.address_line1];
    if (settings.address_line2) parts.push(settings.address_line2);
    parts.push(`${settings.city}, ${settings.province} ${settings.postal_code}`);
    return parts.join(', ');
  }, [settings]);

  const getTodayHours = useCallback(() => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = days[new Date().getDay()];
    const todaySchedule = settings.business_hours.find((s) => s.day === today);
    return todaySchedule ? { day: todaySchedule.day, hours: todaySchedule.hours } : null;
  }, [settings.business_hours]);

  return (
    <PharmacyContext.Provider value={{ settings, loading, error, refresh: fetchSettings, updateSettings, getFullAddress, getTodayHours }}>
      {children}
    </PharmacyContext.Provider>
  );
}

export function usePharmacy() {
  return useContext(PharmacyContext);
}